import React, { useState, useCallback } from "react";
import { t } from "i18next";
import { useDropzone } from "react-dropzone";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { ATTACHEMENTS } from "../../../contants/index";

import { uploadFileToS3 } from "../../../helpers/s3";
import MDButton from "components/Basics/MDButton";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 Mo

const FileUploadDialog = ({
	acceptFile = {
		"image/*": [],
		"application/pdf": [],
		"text/plain": [],
		"application/msword": [],
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document": []
	},
	multiple = true,
	onClose,
	codeLanguage,
	onChangeTypologyInput
}) => {
	const [files, setFiles] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [uploadStatus, setUploadStatus] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);

	const handleClose = () => {
		onClose?.();
	};

	const onDrop = useCallback(acceptedFiles => {
		setFiles(prevFiles => [
			...prevFiles,
			...acceptedFiles.map(file =>
				Object.assign(file, {
					preview: URL.createObjectURL(file),
					id: `${file.name}-${file.size}-${Date.now()}`
				})
			)
		]);
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		multiple: multiple,
		accept: acceptFile,
		maxSize: MAX_FILE_SIZE
	});

	const removeFile = fileId => {
		setFiles(files.filter(file => file.id !== fileId));
	};

	const handleUpload = async () => {
		if (files.length === 0) {
			setUploadStatus({ type: "error", message: "No files selected" });
			return;
		}

		setUploading(true);
		setUploadStatus({ type: "info", message: "Uploading files..." });
		setUploadProgress(0);

		try {
			const uploadPromises = files.map(async (file, index) => {
				// Call your S3 upload function for each file
				try {
					const tempAnswerItemID = `temp_${Date.now()}`;
					const { uploadedFile, fileObject } = await uploadFileToS3(file, tempAnswerItemID, null);

					// Update progress after each file completes
					const newProgress = Math.round(((index + 1) / files.length) * 100);
					setUploadProgress(newProgress);

					return { uploadedFile, fileObject, originalFile: file };
				} catch (error) {
					throw error;
				}
			});

			// Use Promise.all to upload all files concurrently
			const results = await Promise.all(uploadPromises);

			if (onChangeTypologyInput && results?.length) {
				onChangeTypologyInput(
					codeLanguage,
					ATTACHEMENTS,
					results?.map(it => it?.fileObject)
				);
			}

			// setUploadedFiles(results);
			setUploadStatus({
				type: "success",
				message: `Successfully uploaded ${results.length} file${results.length !== 1 ? "s" : ""}!`
			});
			onClose?.();
		} catch (error) {
			setUploadStatus({
				type: "error",
				message: "One or more files failed to upload. Please try again."
			});
		} finally {
			setUploading(false);
		}
	};

	// Helper function to get appropriate icon based on file type
	const getFileIcon = fileType => {
		if (fileType.startsWith("image/")) return <ImageIcon />;
		if (fileType === "application/pdf") return <PictureAsPdfIcon />;
		if (fileType === "text/plain" || fileType.includes("document")) return <DescriptionIcon />;
		return <InsertDriveFileIcon />;
	};

	// Format file size to be human-readable
	const formatFileSize = bytes => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	return (
		<Dialog open onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{t("uploadDialog.title")}
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: theme => theme.palette.grey[500]
					}}
					disabled={uploading}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<DialogContentText paragraph>{t("uploadDialog.instructions")}</DialogContentText>

				<Paper
					{...getRootProps()}
					sx={{
						p: 3,
						border: "2px dashed",
						borderColor: isDragActive ? "secondary.main" : "divider",
						bgcolor: isDragActive ? "action.hover" : "background.paper",
						textAlign: "center",
						cursor: "pointer",
						transition: "all 0.2s ease",
						"&:hover": {
							borderColor: "secondary.main",
							bgcolor: "action.hover"
						}
					}}
				>
					<input {...getInputProps()} />
					<CloudUploadIcon sx={{ fontSize: 48, color: "secondary.main", mb: 2 }} />
					<Typography variant="h6" gutterBottom>
						{isDragActive ? t("uploadDialog.dropHere") : t("uploadDialog.dragDrop")}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{t("uploadDialog.orClick")}s
					</Typography>
				</Paper>

				{files.length > 0 && (
					<Box sx={{ mt: 3 }}>
						<Typography variant="subtitle1" gutterBottom>
							{t("uploadDialog.selectedFiles", { count: files.length })}
						</Typography>
						<List sx={{ maxHeight: "300px", overflow: "auto" }}>
							{files.map(file => (
								<ListItem
									key={file.id}
									sx={{
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
										mb: 1,
										px: 2
									}}
								>
									<ListItemIcon>{getFileIcon(file.type)}</ListItemIcon>
									<ListItemText
										primary={file.name}
										secondary={formatFileSize(file.size)}
										primaryTypographyProps={{
											sx: {
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis"
											}
										}}
									/>
									<ListItemSecondaryAction>
										<IconButton
											aria-label="delete"
											onClick={() => removeFile(file.id)}
											disabled={uploading}
										>
											<DeleteIcon />
										</IconButton>
									</ListItemSecondaryAction>
								</ListItem>
							))}
						</List>
					</Box>
				)}

				{uploading && (
					<Box sx={{ mt: 2 }}>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<Box sx={{ width: "100%", mr: 1 }}>
								<LinearProgress variant="determinate" value={uploadProgress} />
							</Box>
							<Box sx={{ minWidth: 35 }}>
								<Typography
									variant="body2"
									color="text.secondary"
								>{`${uploadProgress}%`}</Typography>
							</Box>
						</Box>
					</Box>
				)}

				{uploadStatus && (
					<Alert severity={uploadStatus.type} sx={{ mt: 2 }}>
						{uploadStatus.message}
					</Alert>
				)}
			</DialogContent>

			<DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
				<MDButton
					variant="outlined"
					color="info"
					onClick={() => setFiles([])}
					disabled={files.length === 0 || uploading}
				>
					{t("uploadDialog.clearAll")}
				</MDButton>
				<Box>
					<MDButton onClick={handleClose} disabled={uploading} variant="outlined" color="info">
						{t("uploadDialog.cancel")}
					</MDButton>
					<MDButton
						onClick={handleUpload}
						color="info"
						variant="contained"
						startIcon={<CloudUploadIcon />}
						disabled={files.length === 0 || uploading}
						sx={{ ml: 1 }}
					>
						{t("uploadDialog.upload")}
					</MDButton>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default FileUploadDialog;
